import FeatureGates, { type LocalOverrides } from '@atlaskit/feature-gate-js-client';

import { OVERRIDES_KEY } from '../constants';

const INITIAL_OVERRIDES: LocalOverrides = { gates: {}, configs: {}, layers: {} };

let cachedOverrides: LocalOverrides = { ...INITIAL_OVERRIDES };

const getLocalStorage = () => {
	try {
		// eslint-disable-next-line no-restricted-properties -- dev-tools FG override storage; must bypass BSC to avoid the circular dependency where BSC's behaviour is itself controlled by these gates.
		return window.localStorage;
	} catch {
		return null;
	}
};

export const getOverridesFromCache = (): LocalOverrides => {
	return { ...cachedOverrides };
};

export const parseStoredOverrides = (): LocalOverrides => {
	const storage = getLocalStorage();
	if (!storage) {
		return { ...INITIAL_OVERRIDES };
	}

	try {
		const stored = storage.getItem(OVERRIDES_KEY);
		if (!stored) {
			return { ...INITIAL_OVERRIDES };
		}
		return JSON.parse(stored);
	} catch {
		return { ...INITIAL_OVERRIDES };
	}
};

const saveOverrides = (overrides: LocalOverrides): void => {
	const storage = getLocalStorage();
	if (!storage) {
		return;
	}

	try {
		storage.setItem(OVERRIDES_KEY, JSON.stringify(overrides));
		cachedOverrides = { ...overrides };
	} catch {
		// Ignore storage errors
	}
};

/**
 * Overrides a specific feature gate with a boolean value.
 *
 * @param name - The name of the feature gate to override
 * @param value - The boolean value to set for the gate
 */
export const overrideGate = (name: string, value: boolean): void => {
	const currentOverrides = getOverridesFromCache();
	currentOverrides.gates[name] = value;
	saveOverrides(currentOverrides);
};

/**
 * Clears the override for a specific feature gate.
 *
 * @param name - The name of the feature gate to clear the override for
 */
export const clearGateOverride = (name: string): void => {
	const currentOverrides = getOverridesFromCache();
	delete currentOverrides.gates[name];
	saveOverrides(currentOverrides);
};

/**
 * Clears all feature gate overrides from localStorage.
 * This removes the entire overrides object from storage.
 */
export const clearAllOverrides = (): void => {
	const storage = getLocalStorage();
	if (!storage) {
		return;
	}

	try {
		storage.removeItem(OVERRIDES_KEY);
		cachedOverrides = { ...INITIAL_OVERRIDES };
	} catch {
		// Ignore storage errors
	}
};

/**
 * Sets multiple overrides at once by merging with existing overrides.
 *
 * @param overrides - A partial LocalOverrides object containing the overrides to set.
 * This will be merged with any existing overrides, with new values taking precedence.
 */
export const setOverrides = (overrides: Partial<LocalOverrides>): void => {
	const currentOverrides = getOverridesFromCache();
	const mergedOverrides: LocalOverrides = {
		gates: { ...currentOverrides.gates, ...overrides.gates },
		configs: { ...currentOverrides.configs, ...overrides.configs },
		layers: { ...currentOverrides.layers, ...overrides.layers },
	};
	saveOverrides(mergedOverrides);
};

/**
 * Checks the value of a feature gate, returning the local override if set,
 * otherwise falling back to the platform feature gate value.
 *
 * @param gateKey - The key of the feature gate to check
 * @returns The boolean value of the gate, either from local override or platform
 */
export const studioFg = (gateKey: string): boolean => {
	const currentOverrides = getOverridesFromCache();

	if (gateKey in currentOverrides.gates) {
		return currentOverrides.gates[gateKey];
	}

	// @NOTE: we need to eslint-disable here, because we have already done:
	// setBooleanFeatureFlagResolver(studioFg) - which will result in an infinite
	// loop, if we use fg() here
	// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
	return FeatureGates.checkGate(gateKey);
};

export const initCacheFromLocalStorage = (): void => {
	const currentOverrides = parseStoredOverrides();
	cachedOverrides = { ...currentOverrides };
};
